import React, { useMemo, useCallback } from 'react'
import { minToString, showMoney } from '@helpers/javascript/date_helpers'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

export const ASC  = "asc"
export const DESC = "desc"
export const SITE = "site"
export const NAME = "name"

const COUNT     = "count"
const TIME      = "time"
const LABOR     = "labor"
const PRODUCTS  = "products"
const PURCHASES = "purchases"
const TOTAL     = "total"
const REVENUE   = "revenue"
const MARGIN    = "margin"
const AMOUNT    = "amount"


export const CostsTable = ({ state, setState, showRevenue }) => {
  const filter = state.filter
  const order  = state.order
  const { totals, lines = [] } = state.data || { totals: {}, lines: [] }

  const getSort = useCallback(() => {
    if (order.direction === ASC) {
      switch (order.column) {
        case NAME:      return (a, b) => a.name.localeCompare(b.name)
        case COUNT:     return (a, b) => a.count - b.count
        case TIME:      return (a, b) => a.time - b.time
        case LABOR:     return (a, b) => a.labor.amount - b.labor.amount
        case PRODUCTS:  return (a, b) => a.products.amount - b.products.amount
        case PURCHASES: return (a, b) => a.purchases.amount - b.purchases.amount
        case TOTAL:     return (a, b) => a.total.amount - b.total.amount
        case REVENUE:   return (a, b) => a.revenue.amount - b.revenue.amount
        case MARGIN:    return (a, b) => a.margin.amount - b.margin.amount
        default:        return () => 0
      }
    } else {
      switch (order.column) {
        case NAME:      return (a, b) => b.name.localeCompare(a.name)
        case COUNT:     return (a, b) => b.count - a.count
        case TIME:      return (a, b) => b.time - a.time
        case LABOR:     return (a, b) => b.labor.amount - a.labor.amount
        case PRODUCTS:  return (a, b) => b.products.amount - a.products.amount
        case PURCHASES: return (a, b) => b.purchases.amount - a.purchases.amount
        case TOTAL:     return (a, b) => b.total.amount - a.total.amount
        case REVENUE:   return (a, b) => b.revenue.amount - a.revenue.amount
        case MARGIN:    return (a, b) => b.margin.amount - a.margin.amount
        default:        return () => 0
      }
    }
  }, [order])

  const baseLines = useMemo(() => {
    let out = lines

    const sum          = array => array.reduce((a, b) => a + b, 0)
    const dig          = (obj, key, secondKey) => secondKey ? obj?.[key]?.[secondKey] : obj?.[key]
    const calculateSum = (key, secondKey) => sum((lines || []).map(line => dig(line, key, secondKey) || 0))
    const diff         = (key, secondKey) => (dig(totals, key, secondKey) || 0) - calculateSum(key, secondKey)

    // Include extra line for workorders without material when filtering by SITE
    if (filter?.asset?.type === SITE) {
      const count = diff(COUNT)
      if (count > 0) {
        out = [
          ...out,
          {
            id: 0,
            name: I18n.t("operational_costs.no_material"),
            count: count,
            time: diff(TIME),
            products: {
              amount:   diff(PRODUCTS, AMOUNT),
              currency: totals?.products?.currency
            },
            labor: {
              amount:   diff(LABOR, AMOUNT),
              currency: totals?.labor?.currency
            },
            purchases: {
              amount:   diff(PURCHASES, AMOUNT),
              currency: totals?.purchases?.currency
            },
            total: {
              amount:   diff(TOTAL, AMOUNT),
              currency: totals?.total?.currency
            }
          }
        ]
      }
    }

    return out
  }, [lines, totals, filter])

  const orderedLines = useMemo(() => {
    const sorter = getSort()
    return [...lines].sort(sorter)
  }, [lines, getSort])

  const changeOrder = column => {
    const direction = column === order.column ? (order.direction === ASC ? DESC : ASC) : ASC
    setState(prev =>({...prev, order: { column, direction }}))
  }

  return (
    <>
      <div className="timesheet__sheet">
        <div
          className='timesheet__sheet__row  timesheet__sheet__row--headers'
          style={{ gridTemplateColumns: `2fr repeat(${showRevenue ? 8 : 6}, 1fr)` }}
        >
          <div className="pointer" onClick={() => { changeOrder(NAME) }}>
            {filter?.asset?.name || I18n.t("client.client")}
            &nbsp;
            {order.column === NAME && order.direction === ASC && <FontAwesomeIcon icon="caret-up" />}
            {order.column === NAME && order.direction === DESC && <FontAwesomeIcon icon="caret-down" />}
          </div>

          <div className="pointer text-center" onClick={() => { changeOrder(COUNT) }}>
            {I18n.t('operational_costs.number')}
            &nbsp;
            {order.column === COUNT && order.direction === ASC && <FontAwesomeIcon icon="caret-up" />}
            {order.column === COUNT && order.direction === DESC && <FontAwesomeIcon icon="caret-down" />}
          </div>

          <div className="pointer text-center" onClick={() => { changeOrder(TIME) }}>
            {I18n.t('operational_costs.time')}
            &nbsp;
            {order.column === TIME && order.direction === ASC && <FontAwesomeIcon icon="caret-up" />}
            {order.column === TIME && order.direction === DESC && <FontAwesomeIcon icon="caret-down" />}
          </div>

          <div className="pointer text-center" onClick={() => { changeOrder(LABOR) }}>
            {I18n.t('operational_costs.labor')}
            &nbsp;
            {order.column === LABOR && order.direction === ASC && <FontAwesomeIcon icon="caret-up" />}
            {order.column === LABOR && order.direction === DESC && <FontAwesomeIcon icon="caret-down" />}
          </div>

          <div className="pointer text-center" onClick={() => { changeOrder(PRODUCTS) }}>
            {I18n.t('operational_costs.products')}
            &nbsp;
            {order.column === PRODUCTS && order.direction === ASC && <FontAwesomeIcon icon="caret-up" />}
            {order.column === PRODUCTS && order.direction === DESC && <FontAwesomeIcon icon="caret-down" />}
          </div>

          <div className="pointer text-center" onClick={() => { changeOrder(PURCHASES) }}>
            {I18n.t('operational_costs.purchases')}
            &nbsp;
            {order.column === PURCHASES && order.direction === ASC && <FontAwesomeIcon icon="caret-up" />}
            {order.column === PURCHASES && order.direction === DESC && <FontAwesomeIcon icon="caret-down" />}
          </div>

          <div className="pointer text-center" onClick={() => { changeOrder(TOTAL) }}>
            {I18n.t('operational_costs.total')}
            &nbsp;
            {order.column === TOTAL && order.direction === ASC && <FontAwesomeIcon icon="caret-up" />}
            {order.column === TOTAL && order.direction === DESC && <FontAwesomeIcon icon="caret-down" />}
          </div>

          {showRevenue && (
            <>
              <div className="pointer text-center" onClick={() => { changeOrder(REVENUE) }}>
                {I18n.t('operational_costs.revenue')}
                &nbsp;
                {order.column === REVENUE && order.direction === ASC && <FontAwesomeIcon icon="caret-up" />}
                {order.column === REVENUE && order.direction === DESC && <FontAwesomeIcon icon="caret-down" />}
              </div>

              <div className="pointer text-center" onClick={() => { changeOrder(MARGIN) }}>
                {I18n.t('operational_costs.margin')}
                &nbsp;
                {order.column === MARGIN && order.direction === ASC && <FontAwesomeIcon icon="caret-up" />}
                {order.column === MARGIN && order.direction === DESC && <FontAwesomeIcon icon="caret-down" />}
              </div>
            </>
          )}
        </div>

        {orderedLines.map(line => (
          <div
            className    = "timesheet__sheet__row timesheet__sheet__row--workorder cursor-pointer"
            style        = {{ gridTemplateColumns: `2fr repeat(${showRevenue ? 8 : 6}, 1fr)` }}
            key          = {line.id}
            data-target  = "#template-modal"
            data-toggle  = {line.id && "modal"}
            data-action  = {line.id && "click->client--modal#show"}
            data-route   = {line.id && `/${line.type}/modal/${line.id}.js`}
          >
            <div>{line.name}</div>
            <div className="text-right">{line.count}</div>
            <div className="text-right">{minToString(line.time)}</div>
            <div className="text-right">{showMoney(line.labor)}</div>
            <div className="text-right">{showMoney(line.product)}</div>
            <div className="text-right">{showMoney(line.purchase)}</div>
            <div className="text-right">{showMoney(line.total)}</div>

            {showRevenue && (
              <>
                <div className="text-right">{showMoney(line.revenue)}</div>
                <div className="text-right">{showMoney(line.margin)}</div>
              </>
            )}
          </div>
        ))}

        {orderedLines.length === 0 && (
          <div className="timesheet__sheet__row timesheet__sheet__row--workorder">
            <div className="text-center g-c-span-row">
              {I18n.t("operational_costs.no_results")}
            </div>
          </div>
        )}
      </div>
    </>
  )
}
